import { FC, useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Typography, useThemeValue } from '@altermaind/web/ui';
import { Form } from '@/_shared/components/Form';
import { convertSizeToNumber } from '@/_shared/helpers';
import { Employer, getUnassignedEmployeesCall } from '../../state';

type NewDepartmentData = {
  name: string;
  managerId?: string;
};

type Props = {
  onFormChange: (data: NewDepartmentData) => void;
  nameError?: boolean;
};

export const NewDepartmentForm: FC<Props> = ({ onFormChange, nameError }) => {
  const t = useTranslations('people.tableEmployees.assignDepartmentBulkDialog');
  const gapM = convertSizeToNumber(`${useThemeValue('space', 'm')}`);
  const { control, watch } = useForm<FieldValues>({
    defaultValues: { newDepartmentName: '', manager: undefined },
  });

  const newDepartmentName = watch('newDepartmentName');
  const manager = watch('manager');

  useEffect(() => {
    onFormChange({ name: newDepartmentName ?? '', managerId: manager?.value });
  }, [newDepartmentName, manager, onFormChange]);

  return (
    <Box mt='m' display='flex' flexDirection='column' style={{ gap: gapM }}>
      <Typography variant='p2/light' color='primary/text'>
        {t('newDepartmentDescription')}
      </Typography>
      <Form.FormInput
        control={control}
        name='newDepartmentName'
        label={t('newDepartmentNameLabel')}
        placeholder={t('newDepartmentNamePlaceholder')}
        additonalProps={{ required: true }}
        hasError={!!nameError}
        errorMessage={nameError ? t('duplicateDepartmentName') : undefined}
      />
      <Form.FormDropdownPaginated
        control={control}
        name='manager'
        label={t('managerLabel')}
        placeholder={t('managerPlaceholder')}
        call={{ ...getUnassignedEmployeesCall }}
        dataMapper={(e: Employer) => ({
          label: `${e.firstName} ${e.lastName}`,
          value: e.id ?? '',
        })}
        searchable
        backgroundColorHeader='grey/light'
        alwaysShowLabel
      />
    </Box>
  );
};
